import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  OverflowMenu, 
  OverflowMenuItem, 
  Popover,
  PopoverContent,
  Button,
  InlineLoading
} from '@carbon/react';
import { Notification, Settings, View } from '@carbon/icons-react';
import { useAlerts } from '../hooks/useAlerts';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import './AlertsBell.scss';

const AlertsBell = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { alerts, alertsTodayCount, isLoading } = useAlerts(20); // Get last 20 alerts
  const [isOpen, setIsOpen] = useState(false);

  // Count unread alerts (status !== 'sent' or recent alerts from last 24 hours)
  const unreadCount = alerts.filter(alert => {
    // Consider alerts as unread if:
    // 1. Status is not 'sent' (pending or failed), OR
    // 2. Alert is from last 24 hours (even if sent, show as recent)
    const alertDate = new Date(alert.created_at);
    const now = new Date();
    const hoursSinceAlert = (now - alertDate) / (1000 * 60 * 60);
    return alert.status !== 'sent' || hoursSinceAlert < 24;
  }).length;

  const handleClearAlerts = async () => {
    if (!isAuthenticated) return;

    try {
      // Get user's sensor IDs
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userSensors } = await supabase
        .from('sensors')
        .select('id')
        .eq('user_id', user.id);

      if (!userSensors || userSensors.length === 0) return;

      const sensorIds = userSensors.map(s => s.id);

      // Mark all recent alerts (last 24 hours) as read by updating their status to 'sent'
      // This effectively clears them from the unread count
      const { error } = await supabase
        .from('alert_notifications')
        .update({ status: 'sent' })
        .in('sensor_id', sensorIds)
        .neq('status', 'sent');

      if (error) {
        console.error('Error clearing alerts:', error);
      } else {
        // Close dropdown and refresh - the useAlerts hook will automatically update via realtime
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error clearing alerts:', error);
    }
  };

  const formatAlertTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getAlertIcon = (alertType) => {
    if (alertType.includes('temperature_max')) return '🌡️';
    if (alertType.includes('temperature_min')) return '❄️';
    if (alertType.includes('humidity_max')) return '💧';
    if (alertType.includes('humidity_min')) return '🏜️';
    return '⚠️';
  };

  const handleAlertClick = (alert) => {
    setIsOpen(false);
    navigate(`/dashboard/devices/${alert.sensor_id}`);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="alerts-bell-wrapper">
      <Popover
        open={isOpen}
        onRequestClose={() => setIsOpen(false)}
        align="bottom-right"
        className="alerts-bell-popover"
      >
        <Button
          kind="ghost"
          hasIconOnly
          iconDescription={`Alerts (${unreadCount} unread)`}
          onClick={() => setIsOpen(!isOpen)}
          data-testid="alerts-bell"
          className="alerts-bell-button"
        >
          <div style={{ position: 'relative' }}>
            <Notification size={20} />
            {unreadCount > 0 && (
              <span className="alerts-badge" data-testid="alerts-count">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </Button>
        <PopoverContent 
          className="alerts-popover-content"
        >
        <div className="alerts-dropdown" data-testid="alerts-dropdown">
          <div className="alerts-dropdown-header">
            <h3>Alerts</h3>
            <div className="alerts-dropdown-actions">
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                iconDescription="Alert Settings"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/dashboard/settings');
                }}
                renderIcon={Settings}
              />
              {alerts.length > 0 && (
                <OverflowMenu
                  flipped
                  ariaLabel="Alert actions"
                  size="sm"
                >
                  <OverflowMenuItem
                    itemText="Clear Alerts"
                    onClick={handleClearAlerts}
                  />
                </OverflowMenu>
              )}
            </div>
          </div>

          <div className="alerts-list">
            {isLoading ? (
              <div className="alerts-loading">
                <InlineLoading description="Loading alerts..." />
              </div>
            ) : alerts.length === 0 ? (
              <div className="alerts-empty">
                <p>No alerts</p>
                <Button
                  kind="primary"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/dashboard/settings');
                  }}
                  renderIcon={Settings}
                >
                  Configure Alerts
                </Button>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`alert-item ${alert.severity}`}
                  onClick={() => handleAlertClick(alert)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAlertClick(alert);
                    }
                  }}
                >
                  <div className="alert-icon">{getAlertIcon(alert.alert_type)}</div>
                  <div className="alert-content">
                    <div className="alert-header">
                      <span className="alert-sensor">{alert.sensor_name}</span>
                      <span className="alert-time">{formatAlertTime(alert.created_at)}</span>
                    </div>
                    <div className="alert-message">{alert.message}</div>
                    {alert.current_value !== undefined && alert.threshold_value !== undefined && (
                      <div className="alert-details">
                        Current: {alert.current_value.toFixed(1)}
                        {alert.alert_type.includes('temp') ? '°C' : '%'} | Threshold:{' '}
                        {alert.threshold_value.toFixed(1)}
                        {alert.alert_type.includes('temp') ? '°C' : '%'}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {alerts.length > 0 && (
            <div className="alerts-dropdown-footer">
              <Button
                kind="tertiary"
                size="sm"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/dashboard/rules');
                }}
                renderIcon={View}
              >
                View All Alerts
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
    </div>
  );
};

export default AlertsBell;

